import { useEffect, useState } from 'react';
import apiService from '../services/api';

const EmbedApiKeySection = ({ schoolId, initialError = '' }) => {
  const [keyInfo, setKeyInfo] = useState(null);
  const [rawKey, setRawKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copyMessage, setCopyMessage] = useState('');
  const [error, setError] = useState(initialError);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(initialError);
      if (!schoolId) {
        setKeyInfo(null);
        setLoading(false);
        if (!initialError) {
          setError('Your account is not assigned to a school.');
        }
        return;
      }

      try {
        const data = await apiService.getSchoolEmbedApiKey(schoolId);
        if (!cancelled) {
          setKeyInfo(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to load integration settings.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [schoolId, initialError]);

  const copyText = async (value, label) => {
    if (!value) return;
    await navigator.clipboard.writeText(value);
    setCopyMessage(`${label} copied`);
    setTimeout(() => setCopyMessage(''), 2200);
  };

  const handleCreateKey = async () => {
    if (!schoolId) return;
    setSaving(true);
    setError('');
    try {
      const data = await apiService.createSchoolEmbedApiKey(schoolId);
      setKeyInfo(data);
      setRawKey(data.rawKey || '');
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to create API key.');
    } finally {
      setSaving(false);
    }
  };

  const handleRotateKey = async () => {
    if (!schoolId) return;
    const ok = window.confirm('Regenerating this key will immediately disable the current key. Continue?');
    if (!ok) return;
    setSaving(true);
    setError('');
    try {
      const data = await apiService.rotateSchoolEmbedApiKey(schoolId);
      setKeyInfo(data);
      setRawKey(data.rawKey || '');
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || err.message || 'Failed to regenerate API key.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section style={{
      marginTop: '32px',
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '16px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px' }}>Homecrowd Embed Integration</h2>
          <p style={{ margin: '8px 0 0', color: '#64748b', maxWidth: '720px' }}>
            Use these values from your backend to request short-lived autologin tokens for the embedded experience.
          </p>
        </div>
        {copyMessage && (
          <div style={{
            background: '#ecfdf5',
            color: '#047857',
            border: '1px solid #a7f3d0',
            borderRadius: '999px',
            padding: '8px 12px',
            fontSize: '14px',
          }}>
            {copyMessage}
          </div>
        )}
      </div>

      {loading ? (
        <p style={{ color: '#64748b', marginTop: '24px' }}>Loading integration settings...</p>
      ) : (
        <>
          {error && (
            <div style={{
              marginTop: '20px',
              background: '#fef2f2',
              color: '#b91c1c',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              padding: '12px 14px',
            }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: '24px', display: 'grid', gap: '18px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px' }}>School ID</label>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <code style={{
                  display: 'block',
                  flex: '1 1 420px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '10px',
                  padding: '12px',
                  overflowWrap: 'anywhere',
                }}>
                  {schoolId || 'Unavailable'}
                </code>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={!schoolId}
                  onClick={() => copyText(schoolId, 'School ID')}
                >
                  Copy School ID
                </button>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontWeight: 700, marginBottom: '8px' }}>School API Key</label>
              {keyInfo?.hasKey ? (
                <div style={{ display: 'grid', gap: '12px' }}>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <code style={{
                      display: 'block',
                      flex: '1 1 420px',
                      background: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      borderRadius: '10px',
                      padding: '12px',
                      overflowWrap: 'anywhere',
                    }}>
                      Prefix: {keyInfo.prefix}
                    </code>
                    <button
                      type="button"
                      className="btn btn-danger"
                      disabled={saving}
                      onClick={handleRotateKey}
                    >
                      {saving ? 'Regenerating...' : 'Regenerate API Key'}
                    </button>
                  </div>
                  <div style={{ color: '#64748b', fontSize: '14px' }}>
                    Created: {keyInfo.createdAt ? new Date(keyInfo.createdAt).toLocaleString() : 'Unknown'}
                    {keyInfo.lastUsedAt ? ` · Last used: ${new Date(keyInfo.lastUsedAt).toLocaleString()}` : ''}
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={saving || !schoolId}
                  onClick={handleCreateKey}
                >
                  {saving ? 'Creating...' : 'Create API Key'}
                </button>
              )}
            </div>

            {rawKey && (
              <div style={{
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '12px',
                padding: '16px',
              }}>
                <strong style={{ display: 'block', color: '#92400e', marginBottom: '8px' }}>
                  Copy this API key now. It will not be shown again.
                </strong>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <code style={{
                    display: 'block',
                    flex: '1 1 420px',
                    background: '#fff7ed',
                    border: '1px solid #fed7aa',
                    borderRadius: '10px',
                    padding: '12px',
                    overflowWrap: 'anywhere',
                  }}>
                    {rawKey}
                  </code>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => copyText(rawKey, 'API key')}
                  >
                    Copy API Key
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
};

export default EmbedApiKeySection;
